import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, Plus, Trash2, Loader2, Check, Search, X, Flame, Timer, Clock,
  Infinity as InfinityIcon, Waves, GripVertical, AlertCircle, Pencil, Clock3,
} from 'lucide-react';
import { toast } from 'sonner';
import { useClientAuth } from '../../../context/ClientAuthContext';
import { estimateCardioSessionMinutes } from './cardioTimeEstimator';

const METHOD_ICONS = { hiit: Flame, tabata: Timer, emom: Clock, amrap: InfinityIcon, intervals: Waves };
const STEPS = ['info', 'sessions', 'recap'];

const emptyBlock = (m) => ({
  method: m?.id || 'hiit', rounds: m?.default_rounds || 8,
  work_seconds: m?.default_work_seconds || 30, rest_seconds: m?.default_rest_seconds || 15,
  time_cap_seconds: m?.id === 'amrap' ? (m?.default_work_seconds || 600) : null,
  exercises: [],
});

const emptySession = (n, methods) => ({
  session_label: `Séance ${n}`, sessions_target: 12, warmup_seconds: 240, cooldown_seconds: 150,
  blocks: [emptyBlock(methods?.[0])],
});

/** Créateur manuel de programme Cardio, en assistant 3 étapes (Infos → Séances/blocs/exercices
 * → Récapitulatif avec badge de temps estimé) — sessions -> blocs (méthode + rounds/effort/repos)
 * -> exercices piochés dans la vraie Bibliothèque Cardio (photos/vidéos déjà générées). */
export const CardioBuilder = ({ en, onBack, onCreated }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const { authFetch } = useClientAuth();

  const [step, setStep] = useState('info');
  const [methods, setMethods] = useState([]);
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('');
  const [weeksTarget, setWeeksTarget] = useState(8);
  const [sessions, setSessions] = useState([]);
  const [pickerFor, setPickerFor] = useState(null); // { sIdx, bIdx }
  const [pickerQuery, setPickerQuery] = useState('');

  useEffect(() => {
    Promise.all([
      authFetch('/api/tracking/cardio-pro/methods').then((r) => r.json()),
      authFetch('/api/tracking/cardio-pro/exercise-library').then((r) => r.json()),
    ]).then(([m, lib]) => {
      setMethods(m.methods || []);
      setLibrary(lib.exercises || []);
      setSessions([emptySession(1, m.methods)]);
    }).finally(() => setLoading(false));
  }, [authFetch]);

  const methodById = useCallback((id) => methods.find((m) => m.id === id), [methods]);

  const updateSession = (sIdx, patch) => setSessions((prev) => prev.map((s, i) => (i === sIdx ? { ...s, ...patch } : s)));
  const updateBlock = (sIdx, bIdx, patch) => setSessions((prev) => prev.map((s, i) => (
    i !== sIdx ? s : { ...s, blocks: s.blocks.map((b, j) => (j === bIdx ? { ...b, ...patch } : b)) }
  )));

  const addSession = () => setSessions((prev) => [...prev, emptySession(prev.length + 1, methods)]);
  const removeSession = (sIdx) => setSessions((prev) => prev.filter((_, i) => i !== sIdx));
  const addBlock = (sIdx) => setSessions((prev) => prev.map((s, i) => (i === sIdx ? { ...s, blocks: [...s.blocks, emptyBlock(methods[0])] } : s)));
  const removeBlock = (sIdx, bIdx) => setSessions((prev) => prev.map((s, i) => (i === sIdx ? { ...s, blocks: s.blocks.filter((_, j) => j !== bIdx) } : s)));

  const setBlockMethod = (sIdx, bIdx, methodId) => {
    const m = methodById(methodId);
    updateBlock(sIdx, bIdx, {
      method: methodId, rounds: m?.default_rounds || 1, work_seconds: m?.default_work_seconds || 30,
      rest_seconds: m?.default_rest_seconds || 0, time_cap_seconds: methodId === 'amrap' ? (m?.default_work_seconds || 600) : null,
    });
  };

  const addExercise = (sIdx, bIdx, exName) => setSessions((prev) => prev.map((s, i) => (
    i !== sIdx ? s : {
      ...s,
      blocks: s.blocks.map((b, j) => (j !== bIdx ? b : {
        ...b,
        exercises: b.exercises.some((e) => e.name === exName) ? b.exercises : [...b.exercises, { name: exName, notes: '' }],
      })),
    }
  )));
  const removeExercise = (sIdx, bIdx, exIdx) => setSessions((prev) => prev.map((s, i) => (
    i !== sIdx ? s : { ...s, blocks: s.blocks.map((b, j) => (j !== bIdx ? b : { ...b, exercises: b.exercises.filter((_, k) => k !== exIdx) })) }
  )));

  const infoError = () => {
    if (!name.trim()) return L('Merci de donner un nom au programme.', 'Please name your program.');
    if (!goal.trim()) return L('Merci de préciser un objectif.', 'Please specify a goal.');
    return null;
  };

  const sessionsError = () => {
    if (sessions.length === 0) return L('Ajoutez au moins une séance.', 'Add at least one session.');
    for (let i = 0; i < sessions.length; i++) {
      const s = sessions[i];
      if (!s.session_label.trim()) return L(`La séance ${i + 1} doit avoir un nom.`, `Session ${i + 1} needs a name.`);
      if (s.blocks.length === 0) return L(`La séance "${s.session_label}" doit avoir au moins un bloc.`, `Session "${s.session_label}" needs at least one block.`);
      for (let j = 0; j < s.blocks.length; j++) {
        if (s.blocks[j].exercises.length === 0) {
          return L(`Ajoutez au moins 1 exercice au bloc ${j + 1} de "${s.session_label}".`, `Add at least 1 exercise to block ${j + 1} of "${s.session_label}".`);
        }
      }
    }
    return null;
  };

  const goNext = () => {
    if (step === 'info') {
      const err = infoError();
      if (err) { toast.error(err); return; }
      setStep('sessions');
    } else if (step === 'sessions') {
      const err = sessionsError();
      if (err) { toast.error(err); return; }
      setStep('recap');
    }
  };
  const goBackStep = () => {
    if (step === 'sessions') setStep('info');
    else if (step === 'recap') setStep('sessions');
    else onBack();
  };

  const currentError = step === 'info' ? infoError() : step === 'sessions' ? sessionsError() : null;
  const totalMinutes = sessions.reduce((sum, s) => sum + estimateCardioSessionMinutes(s), 0);

  const submit = () => {
    const err = sessionsError() || infoError();
    if (err) { toast.error(err); return; }
    setSaving(true);
    const body = { name: name.trim(), goal: goal.trim(), weeks_target: +weeksTarget, sessions };
    authFetch('/api/tracking/cardio-pro', { method: 'POST', body: JSON.stringify(body) })
      .then((r) => (r.ok ? r.json() : r.json().then((j) => Promise.reject(j))))
      .then(() => { toast.success(L('Programme cardio créé !', 'Cardio program created!')); onCreated(); })
      .catch(() => toast.error(L('Erreur lors de la création.', 'Error creating the program.')))
      .finally(() => setSaving(false));
  };

  const filteredLibrary = library.filter((e) => e.name_fr.toLowerCase().includes(pickerQuery.toLowerCase()));

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-orange-400" /></div>;

  const stepIdx = STEPS.indexOf(step);

  return (
    <div className="space-y-5 pb-24" data-testid="cardio-builder">
      <button onClick={goBackStep} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="cardio-builder-back-btn">
        <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
      </button>
      <div>
        <h2 className="text-xl font-black text-white flex items-center gap-2"><Flame className="w-5 h-5 text-orange-400" /> {L('Créer mon programme Cardio', 'Create my Cardio program')}</h2>
        <p className="text-xs text-zinc-500 mt-1" data-testid="cardio-builder-step-label">
          {L('Étape', 'Step')} {stepIdx + 1}/3 · {step === 'info' ? L('Infos', 'Info') : step === 'sessions' ? L('Séances', 'Sessions') : L('Récapitulatif', 'Summary')}
        </p>
        <div className="flex items-center gap-1.5 mt-2" data-testid="cardio-builder-progress">
          {STEPS.map((s, i) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= stepIdx ? 'bg-gradient-to-r from-orange-500 to-red-500' : 'bg-white/10'}`} />
          ))}
        </div>
      </div>

      {step === 'info' && (
        <motion.div key="info" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} className="rounded-2xl bg-white/[0.035] border border-white/10 p-4 space-y-3">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={L('Nom du programme (ex: Brûle-graisses 6 semaines)', 'Program name')}
            className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder:text-zinc-600 focus:border-orange-500/50 focus:outline-none" data-testid="cardio-builder-name-input" />
          <input value={goal} onChange={(e) => setGoal(e.target.value)} placeholder={L('Objectif (ex: Perte de gras, endurance...)', 'Goal')}
            className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder:text-zinc-600 focus:border-orange-500/50 focus:outline-none" data-testid="cardio-builder-goal-input" />
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500">{L('Durée du programme (semaines)', 'Program duration (weeks)')}</span>
            <input type="number" min={1} max={52} value={weeksTarget} onChange={(e) => setWeeksTarget(e.target.value)}
              className="w-20 bg-zinc-900 border border-white/10 rounded-lg px-2 py-1 text-white text-sm text-center" data-testid="cardio-builder-weeks-input" />
          </div>
        </motion.div>
      )}

      {step === 'sessions' && (
        <motion.div key="sessions" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
          {sessions.map((s, sIdx) => (
            <div key={sIdx} className="rounded-2xl bg-white/[0.035] border border-white/10 p-4 space-y-3" data-testid={`cardio-builder-session-${sIdx}`}>
              <div className="flex items-center gap-2">
                <GripVertical className="w-4 h-4 text-zinc-600 shrink-0" />
                <input value={s.session_label} onChange={(e) => updateSession(sIdx, { session_label: e.target.value })}
                  className="flex-1 bg-zinc-900 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm font-bold focus:border-orange-500/50 focus:outline-none" data-testid={`cardio-session-label-${sIdx}`} />
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold bg-orange-500/15 text-orange-300 border border-orange-500/30 shrink-0" data-testid={`cardio-session-time-badge-${sIdx}`}>
                  <Clock3 className="w-3 h-3" /> ~{estimateCardioSessionMinutes(s)} min
                </span>
                {sessions.length > 1 && (
                  <button onClick={() => removeSession(sIdx)} className="text-zinc-500 hover:text-red-400 shrink-0" data-testid={`cardio-session-remove-${sIdx}`}><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
              <div className="flex items-center gap-3 flex-wrap text-xs text-zinc-400">
                <label className="flex items-center gap-1.5">{L('Échauffement (s)', 'Warmup (s)')}
                  <input type="number" min={0} max={1800} value={s.warmup_seconds} onChange={(e) => updateSession(sIdx, { warmup_seconds: +e.target.value })}
                    className="w-16 bg-zinc-900 border border-white/10 rounded px-1.5 py-1 text-white text-center" data-testid={`cardio-warmup-${sIdx}`} />
                </label>
                <label className="flex items-center gap-1.5">{L('Retour au calme (s)', 'Cooldown (s)')}
                  <input type="number" min={0} max={1800} value={s.cooldown_seconds} onChange={(e) => updateSession(sIdx, { cooldown_seconds: +e.target.value })}
                    className="w-16 bg-zinc-900 border border-white/10 rounded px-1.5 py-1 text-white text-center" data-testid={`cardio-cooldown-${sIdx}`} />
                </label>
              </div>

              {s.blocks.map((b, bIdx) => {
                const Icon = METHOD_ICONS[b.method] || Flame;
                return (
                  <div key={bIdx} className="rounded-xl bg-zinc-900/60 border border-white/10 p-3 space-y-2.5" data-testid={`cardio-block-${sIdx}-${bIdx}`}>
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-orange-400 shrink-0" />
                      <select value={b.method} onChange={(e) => setBlockMethod(sIdx, bIdx, e.target.value)}
                        className="flex-1 bg-zinc-950 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs font-semibold focus:border-orange-500/50 focus:outline-none" data-testid={`cardio-block-method-${sIdx}-${bIdx}`}>
                        {methods.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                      </select>
                      {s.blocks.length > 1 && (
                        <button onClick={() => removeBlock(sIdx, bIdx)} className="text-zinc-500 hover:text-red-400 shrink-0" data-testid={`cardio-block-remove-${sIdx}-${bIdx}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap text-[11px] text-zinc-400">
                      {b.method === 'amrap' ? (
                        <label className="flex items-center gap-1.5">{L('Temps limite (s)', 'Time cap (s)')}
                          <input type="number" min={30} max={3600} value={b.time_cap_seconds || 600}
                            onChange={(e) => updateBlock(sIdx, bIdx, { time_cap_seconds: +e.target.value, work_seconds: +e.target.value })}
                            className="w-16 bg-zinc-950 border border-white/10 rounded px-1.5 py-1 text-white text-center" data-testid={`cardio-block-timecap-${sIdx}-${bIdx}`} />
                        </label>
                      ) : (
                        <label className="flex items-center gap-1.5">{L('Tours', 'Rounds')}
                          <input type="number" min={1} max={50} value={b.rounds} onChange={(e) => updateBlock(sIdx, bIdx, { rounds: +e.target.value })}
                            className="w-14 bg-zinc-950 border border-white/10 rounded px-1.5 py-1 text-white text-center" data-testid={`cardio-block-rounds-${sIdx}-${bIdx}`} />
                        </label>
                      )}
                      <label className="flex items-center gap-1.5">{L('Effort (s)', 'Work (s)')}
                        <input type="number" min={5} max={1200} value={b.work_seconds} onChange={(e) => updateBlock(sIdx, bIdx, { work_seconds: +e.target.value })}
                          className="w-16 bg-zinc-950 border border-white/10 rounded px-1.5 py-1 text-white text-center" data-testid={`cardio-block-work-${sIdx}-${bIdx}`} />
                      </label>
                      {b.method !== 'amrap' && (
                        <label className="flex items-center gap-1.5">{L('Repos (s)', 'Rest (s)')}
                          <input type="number" min={0} max={600} value={b.rest_seconds} onChange={(e) => updateBlock(sIdx, bIdx, { rest_seconds: +e.target.value })}
                            className="w-14 bg-zinc-950 border border-white/10 rounded px-1.5 py-1 text-white text-center" data-testid={`cardio-block-rest-${sIdx}-${bIdx}`} />
                        </label>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {b.exercises.map((ex, exIdx) => (
                        <span key={exIdx} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-orange-500/15 border border-orange-500/30 text-orange-200 text-[11px]" data-testid={`cardio-exercise-chip-${sIdx}-${bIdx}-${exIdx}`}>
                          {ex.name}
                          <button onClick={() => removeExercise(sIdx, bIdx, exIdx)} data-testid={`cardio-exercise-remove-${sIdx}-${bIdx}-${exIdx}`}><X className="w-3 h-3" /></button>
                        </span>
                      ))}
                      <button onClick={() => { setPickerFor({ sIdx, bIdx }); setPickerQuery(''); }}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-dashed border-white/20 text-zinc-400 text-[11px] hover:border-orange-500/40 hover:text-orange-300"
                        data-testid={`cardio-exercise-add-btn-${sIdx}-${bIdx}`}>
                        <Plus className="w-3 h-3" /> {L('Exercice', 'Exercise')}
                      </button>
                    </div>
                  </div>
                );
              })}
              <button onClick={() => addBlock(sIdx)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-orange-300 hover:text-orange-200" data-testid={`cardio-block-add-${sIdx}`}>
                <Plus className="w-3.5 h-3.5" /> {L('Ajouter un bloc', 'Add a block')}
              </button>
            </div>
          ))}

          <button onClick={addSession} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/10 text-zinc-300 text-xs font-semibold hover:border-orange-500/40" data-testid="cardio-session-add-btn">
            <Plus className="w-3.5 h-3.5" /> {L('Ajouter une séance', 'Add a session')}
          </button>
        </motion.div>
      )}

      {step === 'recap' && (
        <motion.div key="recap" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} className="space-y-4" data-testid="cardio-builder-recap">
          <div className="rounded-2xl bg-white/[0.035] border border-white/10 p-4 flex items-start justify-between gap-3">
            <div>
              <p className="text-white font-bold text-lg">{name}</p>
              <p className="text-sm text-zinc-500">{goal}</p>
              <p className="text-xs text-zinc-600 mt-1">{weeksTarget} {L('semaines', 'weeks')}</p>
            </div>
            <button onClick={() => setStep('info')} className="text-zinc-500 hover:text-orange-300 shrink-0" data-testid="cardio-recap-edit-info-btn"><Pencil className="w-4 h-4" /></button>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-zinc-300">{sessions.length} {L('séance(s)', 'session(s)')}</p>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black bg-orange-500/15 text-orange-300 border border-orange-500/30" data-testid="cardio-recap-total-time-badge">
              <Clock3 className="w-3.5 h-3.5" /> ~{totalMinutes} min {L('au total', 'total')}
            </span>
          </div>
          {sessions.map((s, sIdx) => (
            <div key={sIdx} className="rounded-2xl bg-white/[0.035] border border-white/10 p-4 space-y-2" data-testid={`cardio-recap-session-${sIdx}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-white font-bold text-sm">{s.session_label}</p>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-orange-500/15 text-orange-300 border border-orange-500/30">
                    <Clock3 className="w-3 h-3" /> ~{estimateCardioSessionMinutes(s)} min
                  </span>
                  <button onClick={() => setStep('sessions')} className="text-zinc-500 hover:text-orange-300" data-testid={`cardio-recap-edit-session-${sIdx}`}><Pencil className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {s.blocks.map((b, bIdx) => {
                  const Icon = METHOD_ICONS[b.method] || Flame;
                  const label = methods.find((m) => m.id === b.method)?.label || b.method;
                  return (
                    <span key={bIdx} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-zinc-900/60 border border-white/10 text-zinc-300 text-[11px]">
                      <Icon className="w-3 h-3 text-orange-400" /> {label}
                    </span>
                  );
                })}
              </div>
            </div>
          ))}
        </motion.div>
      )}

      <div className="fixed bottom-0 inset-x-0 z-40 bg-zinc-950/95 backdrop-blur-md border-t border-white/10 p-3 flex flex-col items-center gap-1.5">
        {currentError && (
          <p className="text-amber-400 text-xs font-semibold flex items-center gap-1.5" data-testid="cardio-builder-validation-hint">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {currentError}
          </p>
        )}
        {step === 'recap' ? (
          <button onClick={submit} disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-orange-500 to-red-500 hover:scale-105 transition-transform disabled:opacity-50 disabled:hover:scale-100"
            data-testid="cardio-builder-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {L('Créer le programme', 'Create program')}
          </button>
        ) : (
          <button onClick={goNext}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-orange-500 to-red-500 hover:scale-105 transition-transform"
            data-testid="cardio-builder-next-btn">
            {L('Continuer', 'Continue')} <Check className="w-4 h-4" />
          </button>
        )}
      </div>

      <AnimatePresence>
        {pickerFor && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setPickerFor(null)} data-testid="cardio-exercise-picker-modal">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={(e) => e.stopPropagation()} className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
              <div className="p-4 border-b border-white/10 flex items-center gap-2">
                <Search className="w-4 h-4 text-zinc-500" />
                <input autoFocus value={pickerQuery} onChange={(e) => setPickerQuery(e.target.value)} placeholder={L('Rechercher un exercice cardio...', 'Search a cardio exercise...')}
                  className="flex-1 bg-transparent text-white text-sm placeholder:text-zinc-600 focus:outline-none" data-testid="cardio-exercise-picker-search" />
                <button onClick={() => setPickerFor(null)} data-testid="cardio-exercise-picker-close"><X className="w-4 h-4 text-zinc-500" /></button>
              </div>
              <div className="overflow-y-auto p-2 space-y-1">
                {filteredLibrary.map((ex) => (
                  <button key={ex.id} onClick={() => { addExercise(pickerFor.sIdx, pickerFor.bIdx, ex.name_fr); setPickerFor(null); }}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 flex items-center gap-3" data-testid={`cardio-exercise-option-${ex.id}`}>
                    {ex.image_url ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${ex.image_url}`} alt={ex.name_fr} className="w-9 h-9 rounded-lg object-contain bg-zinc-950 border border-white/10" />
                    ) : <div className="w-9 h-9 rounded-lg bg-zinc-800" />}
                    <div>
                      <p className="text-white text-sm">{ex.name_fr}</p>
                      <p className="text-[10px] text-zinc-500">{ex.muscle_group}</p>
                    </div>
                  </button>
                ))}
                {filteredLibrary.length === 0 && <p className="text-center text-zinc-500 text-sm py-6">{L('Aucun résultat.', 'No results.')}</p>}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CardioBuilder;
